import campaignCodeActions from './campaign-code-actions';
import { webApiClient } from 'tradera/utils/http';
import { selectCategoryAttributes } from '../selectors/attribute-selectors';
import { getSyiSlice } from 'tradera/apps/syi/state/selectors/syi';
import ThrottledAction from 'tradera/apps/syi/utils/throttled-action';
import { logger } from 'packages/logger';
import { selectPreferredLanguage } from 'tradera/state/language/selectors';
import * as imageActions from './image-actions';
import {
    resetAttributes,
    resetImageAttributeSuggestions,
    setCategoryAttributeOption,
    setCategoryAttributes,
    setCategoryAttributesSuggestions,
    setDraftAttributes,
    setImageToColorSuggestions,
    updateCategoryAttributesOptionsStatuses,
    autoFillSuggestedAttributes
} from '../reducers/attributes';

const throttleTimeout = 2000;
const suggestionsThrottle = new ThrottledAction(throttleTimeout, true);
const imageToColorsThrottle = new ThrottledAction(throttleTimeout, false);

const INPUT_TYPE_MAP = {
    Button: 'button',
    Color: 'color',
    Dropdown: 'dropdown'
};

const COLOR_ATTRIBUTE_ID = 2;

const transformServerOption = (serverOption) => {
    return {
        value:
            typeof serverOption === 'string'
                ? serverOption
                : serverOption.value,
        sortOrder: serverOption.sortOrder,
        isActive: false,
        warningLabel: {
            title:
                serverOption.warningLabel !== undefined &&
                serverOption.warningLabel !== null
                    ? serverOption.warningLabel.title
                    : null,
            text:
                serverOption.warningLabel !== undefined &&
                serverOption.warningLabel !== null
                    ? serverOption.warningLabel.text
                    : null
        }
    };
};

const transformServerAttribute = (attribute) => {
    return {
        id: attribute.id,
        title: attribute.displayName,
        description: attribute.description,
        sortOrder: attribute.sortOrder,
        backingField: attribute.backingField,
        options: attribute.possibleValues.map(transformServerOption),
        inputType: INPUT_TYPE_MAP[attribute.type],
        isMultiSelect: attribute.isMultiSelect,
        maxNumberOfValues: attribute.maxNumberOfValues,
        minNumberOfValues: attribute.minNumberOfValues,
        numberOfValuesSelected: 0,
        shouldTranslateValues: attribute.shouldTranslateValues,
        preserveOptionsOrder: attribute.preserveOptionsOrder
    };
};

export const setAttributesFromDraft = (draft) => (dispatch) => {
    dispatch(setDraftAttributes(draft.attributeValues));
};

export const reset = () => (dispatch) => {
    dispatch(resetAttributes());
};

export const fetchForCategory = (categoryId) => async (dispatch, getState) => {
    dispatch(reset());

    if (!categoryId) {
        return;
    }

    try {
        const { languageCodeIso2 } = selectPreferredLanguage(getState());
        const response = await webApiClient.get(
            `/selling/attributes/?categoryId=${categoryId}&locale=${languageCodeIso2}`
        );
        dispatch(
            setCategoryAttributes(
                response.data.attributeDefinitions.map(transformServerAttribute)
            )
        );
        dispatch(updateCategoryAttributesOptionsStatuses());
        dispatch(campaignCodeActions.validateCampaignCode());
        dispatch(imageActions.fetchImageAttributeSuggestions());
    } catch (error) {
        dispatch(reset());
        throw error;
    }
};

export const updateAttribute =
    (attributeId, optionValue, activate, onUpdated) => (dispatch, getState) => {
        const { maxNumberOfValues, numberOfValuesSelected, isMultiSelect } =
            selectCategoryAttributes(getState()).find(
                (a) => a.id === attributeId
            );

        if (
            activate &&
            isMultiSelect &&
            maxNumberOfValues === numberOfValuesSelected
        ) {
            return;
        }

        dispatch(
            setCategoryAttributeOption({
                attributeId,
                optionValue,
                activate
            })
        );
        dispatch(campaignCodeActions.validateCampaignCode());
        if (onUpdated) {
            onUpdated();
        }
    };

export const resetAttribute = (attributeId) => (dispatch) => {
    dispatch(
        setCategoryAttributeOption({
            attributeId,
            activate: false
        })
    );
    dispatch(campaignCodeActions.validateCampaignCode());
};

export const throttledFetchCategoryAttributeSuggestions =
    (categoryId, shortDescription) => (dispatch) => {
        const action = () => {
            dispatch(
                fetchCategoryAttributeSuggestions(categoryId, shortDescription)
            );
        };
        suggestionsThrottle.execute(action);
    };

export const throttledFetchImageAttributeSuggestions =
    (draftImageIds) => (dispatch, getState) => {
        const featureSwitches = getState().environment.featureSwitches;
        const draftCategoryId =
            getSyiSlice(getState())?.category?.draftCategoryId;
        const categoryAttributes =
            getSyiSlice(getState())?.attributes?.categoryAttributes;
        const hasColorAttribute =
            categoryAttributes &&
            categoryAttributes.some(
                (attribute) => attribute.backingField === 'color'
            );

        if (
            !featureSwitches['syi-image-attribute-suggestions'] ||
            !draftImageIds ||
            draftImageIds.length === 0 ||
            !draftCategoryId ||
            draftCategoryId === 0 ||
            !hasColorAttribute
        ) {
            return;
        }
        const action = () => {
            dispatch(fetchImageAttributeSuggestions(draftImageIds));
        };
        imageToColorsThrottle.execute(action);
    };

const fetchImageAttributeSuggestions = (draftImageIds) => async (dispatch) => {
    dispatch(resetImageAttributeSuggestions());

    const paramKeyPart = 'imageIds=';
    const query = paramKeyPart + draftImageIds.join(`&${paramKeyPart}`);

    try {
        const response = await webApiClient.get(
            `/selling/image-colors?${query}`
        );

        const colors = response.data?.colorValues?.map((cv) => cv.color) || [];

        dispatch(
            setImageToColorSuggestions({
                suggestedAttributes: [
                    {
                        id: COLOR_ATTRIBUTE_ID,
                        name: 'color',
                        values: colors
                    }
                ]
            })
        );
    } catch (error) {
        logger(error);
    }
};

export const fetchCategoryAttributeSuggestions =
    (categoryId, shortDescription) => async (dispatch, getState) => {
        const featureSwitches = getState().environment.featureSwitches;
        if (!featureSwitches['syi-attribute-suggestions']) {
            return;
        }

        if (!categoryId || !shortDescription) {
            return;
        }

        const splitTestGroup =
            getState().environment.splitTestGroups[
                'attribute_suggestion_extractor'
            ];
        if (!splitTestEnabled(splitTestGroup)) {
            return;
        }
        const extractorType = getAttributeSuggestionExtractor(splitTestGroup);

        try {
            const params = {
                categoryId,
                shortDescription,
                extractorType
            };
            const response = await webApiClient.post(
                '/selling/suggest/attributes',
                params
            );

            dispatch(
                setCategoryAttributesSuggestions({
                    suggestedAttributes:
                        response.data.attributeValues.termAttributeValues.map(
                            transformServerAttributeSuggestion
                        ),
                    suggestionsExtractorType: response.data.extractorType
                })
            );
            dispatch(autoFillSuggestedAttributes());
        } catch (error) {
            dispatch({
                type: 'RESET_ATTRIBUTES'
            });
            throw error;
        }
    };

const splitTestEnabled = (splitTest) => splitTest !== 'control';

const getAttributeSuggestionExtractor = (splitTest) => {
    switch (splitTest) {
        case 'code':
            return 0;
        case 'tradera':
            return 1;
        case 'google':
            return 2;
        default:
            return 0;
    }
};

const transformServerAttributeSuggestion = (attributeSuggestion) => {
    return {
        id: attributeSuggestion.id,
        name: attributeSuggestion.name,
        values: attributeSuggestion.values
    };
};
